// Fechas del evento en español de Chile, siempre en la zona horaria del evento (IANA), nunca en la del navegador.
// Así un evento en Punta Arenas o Rapa Nui se lee con su hora local aunque se abra desde Santiago.

export interface EventDateText {
  /** "Jueves 13 de noviembre" */
  dayLong: string;
  /** "Jue 13 nov" */
  dayShort: string;
  /** "18:30 – 20:30" (o "18:30 – vie 14, 01:00" si cruza la medianoche) */
  timeRange: string;
  /** "18:30" */
  startTime: string;
}

const ZONE_LABEL: Record<string, string> = {
  'America/Santiago': 'hora de Chile',
  'America/Punta_Arenas': 'hora de Magallanes',
  'Pacific/Easter': 'hora de Rapa Nui',
};

/** "hora de Chile", "hora de Rapa Nui"… o el nombre IANA si no es una zona chilena conocida. */
export function zoneLabel(timeZone: string): string {
  return ZONE_LABEL[timeZone] ?? `hora ${timeZone}`;
}

const cap = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

function parts(date: Date, timeZone: string, opts: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('es-CL', { timeZone, ...opts }).formatToParts(date)) out[p.type] = p.value;
  return out;
}

export function formatEventDates(startsAt: string, endsAt: string, timeZone: string): EventDateText {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) throw new Error('fecha inválida');

  const s = parts(start, timeZone, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const e = parts(end, timeZone, { weekday: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const short = parts(start, timeZone, { weekday: 'short', day: 'numeric', month: 'short' });
  const startDay = parts(start, timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' });
  const endDay = parts(end, timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' });
  const sameDay = startDay.year === endDay.year && startDay.month === endDay.month && startDay.day === endDay.day;

  const startTime = `${s.hour}:${s.minute}`;
  const endTime = `${e.hour}:${e.minute}`;
  const clean = (v: string | undefined) => (v ?? '').replace('.', '');

  return {
    dayLong: `${cap(s.weekday ?? '')} ${s.day} de ${s.month}`,
    dayShort: `${cap(clean(short.weekday))} ${short.day} ${clean(short.month)}`,
    timeRange: sameDay ? `${startTime} – ${endTime}` : `${startTime} – ${clean(e.weekday)} ${e.day}, ${endTime}`,
    startTime,
  };
}
